/* shared/_bridge — compatibility bridge.
   ─────────────────────────────────────────────────────────────
   Re-exposes ESM shared-layer modules and the Phase 7A API client
   on `window` so bridged tier files resolve their dependencies unchanged.
   Must be imported before any bridged file. */
import React from 'react';
import * as ReactDOM from 'react-dom';
import * as ReactDOMClient from 'react-dom/client';
import * as XLSX from 'xlsx';

import { NexusIcon } from './ui/NexusIcon';
import { Avatar, Pill, Btn, IconBtn, Kbd, Score, COMPANY_SCORE_BANDS, getScoreBand } from './ui';
import { ToastProvider, useToast } from './feedback';
import { StoreProvider, useStore } from './state';
import { NexusTheme } from './theme';
import { NEXUS_DATA, NEXUS_BFSI } from './data';
import {
  CountUp, Sparkline, RingMeter, OwnerAvatar,
  useDashboardLayout, PremiumPanel, seededSeries, hashKey,
} from './charts';
import {
  Sidebar, Topbar, ModuleHeader, AppLayout,
  SettingsWorkspaceLayout, AuthLayout, useLayoutCtx,
} from './layout';
import {
  exportCSV, useTableState, useFilters, FilterChip,
  BulkBar, RowActions, SortableTh,
} from './data-table';
import {
  useDirtyForm, RecordFormDrawer, CompanyPicker, QuickCompanyModal,
} from './forms';
import { ConfirmModal } from './overlays';

/* Phase 7A — API layer */
import apiClient from '../api/client.js';
import * as authApi      from '../api/auth.api.js';
import * as sessionApi   from '../api/session.api.js';
import * as platformApi  from '../api/platform.api.js';
import * as orgApi       from '../api/organization.api.js';
import * as workspaceApi from '../api/workspace.api.js';
import * as peopleApi    from '../api/people.api.js';
import * as companyApi   from '../api/company.api.js';
import * as dealApi      from '../api/deal.api.js';
import * as taskApi      from '../api/task.api.js';
import * as noteApi      from '../api/note.api.js';
import * as documentApi  from '../api/document.api.js';
import * as analyticsApi from '../api/analytics.api.js';
import * as brandingApi  from '../api/branding.api.js';
import * as partnerApi   from '../api/partner.api.js';
import * as licenseApi   from '../api/license.api.js';
import * as auditApi     from '../api/audit.api.js';
import * as activityApi      from '../api/activity.api.js';
import * as stageApi         from '../api/stage.api.js';
import * as crmPipelinesApi  from '../api/crm-pipelines.api.js';
import * as crmSearchApi     from '../api/crm-search.api.js';
import * as customFieldsApi  from '../api/crm-custom-fields.api.js';
import * as savedViewApi     from '../api/savedview.api.js';
import * as enums            from '../api/enums.js';
import * as dealEnrichmentApi from '../api/deal-enrichment.api.js';
import * as analyticsP3Api   from '../api/analytics-p3.api.js';
import * as emailApi          from '../api/email.api.js';
import * as meetingsApi       from '../api/meetings.api.js';
import * as collaborationApi           from '../api/collaboration.api.js';
import * as notificationsApi           from '../api/notifications.api.js';
import * as notificationPreferencesApi from '../api/notification-preferences.api.js';
import * as workflowApi       from '../api/workflow.api.js';
import * as commAnalyticsApi  from '../api/comm-analytics.api.js';

/* Phase 5 — AI, Automation, Commerce, Analytics, Extensions */
import * as aiApi            from '../api/ai.api.js';
import * as automationApi    from '../api/automation.api.js';
import * as customObjectsApi from '../api/custom-objects.api.js';
import * as formsApi         from '../api/forms.api.js';
import * as webhooksApi      from '../api/webhooks.api.js';
import * as importExportApi  from '../api/import-export.api.js';
import * as jobsApi          from '../api/jobs.api.js';
import * as pluginsApi       from '../api/plugins.api.js';
import * as reportsApi       from '../api/reports.api.js';
import * as goalsApi         from '../api/goals.api.js';
import * as territoryApi     from '../api/territory.api.js';
import * as playbooksApi     from '../api/playbooks.api.js';
import * as priceBooksApi    from '../api/price-books.api.js';
import * as quotesApi        from '../api/quotes.api.js';
import * as approvalsApi     from '../api/approvals.api.js';
import * as healthScoreApi   from '../api/health-score.api.js';

const g = window;

/* Core runtime */
g.React    = React;
g.ReactDOM = { ...ReactDOM, ...ReactDOMClient };
g.XLSX     = XLSX;

/* Design-system globals expected by bridged tier files */
Object.assign(g, {
  NexusIcon, Avatar, Pill, Btn, IconBtn, Kbd, Score, COMPANY_SCORE_BANDS, getScoreBand,
  ToastProvider, useToast,
  StoreProvider, useStore,
  NexusTheme,
  NEXUS_DATA, NEXUS_BFSI,
  CountUp, Sparkline, RingMeter, OwnerAvatar, useDashboardLayout, PremiumPanel, seededSeries, hashKey,
  Sidebar, Topbar, ModuleHeader, AppLayout, SettingsWorkspaceLayout, AuthLayout, useLayoutCtx,
  exportCSV, useTableState, useFilters, FilterChip, BulkBar, RowActions, SortableTh,
  useDirtyForm, RecordFormDrawer, CompanyPicker, QuickCompanyModal,
  ConfirmModal,
});

/* Phase 7A — centralized API layer exposed for bridged components */
g.__nx_api = {
  client:   apiClient,
  auth:     authApi,
  session:  sessionApi,
  platform: platformApi,
  org:      orgApi,
  workspace: workspaceApi,
  people:   peopleApi,
  company:  companyApi,
  deal:     dealApi,
  task:     taskApi,
  note:     noteApi,
  document: documentApi,
  analytics: analyticsApi,
  branding: brandingApi,
  partner:  partnerApi,
  license:   licenseApi,
  audit:     auditApi,
  activity:     activityApi,
  stages:       stageApi,
  crmPipelines: crmPipelinesApi,
  crmSearch:    crmSearchApi,
  customFields:    customFieldsApi,
  savedViews:      savedViewApi,
  enums,
  dealEnrichment:  dealEnrichmentApi,
  analyticsP3:     analyticsP3Api,
  email:           emailApi,
  meetings:        meetingsApi,
  collaboration:          collaborationApi,
  notifications:          notificationsApi,
  notificationPreferences: notificationPreferencesApi,
  workflow:        workflowApi,
  commAnalytics:   commAnalyticsApi,
  /* Phase 5 */
  ai:              aiApi,
  automation:      automationApi,
  customObjects:   customObjectsApi,
  forms:           formsApi,
  webhooks:        webhooksApi,
  importExport:    importExportApi,
  jobs:            jobsApi,
  plugins:         pluginsApi,
  reports:         reportsApi,
  goals:           goalsApi,
  territory:       territoryApi,
  playbooks:       playbooksApi,
  priceBooks:      priceBooksApi,
  quotes:          quotesApi,
  approvals:       approvalsApi,
  healthScore:     healthScoreApi,
};

